import { Button, Empty, EmptyDescription, EmptyHeader, EmptyTitle } from "@beweegbaas/ui";
import { createFileRoute, notFound, useRouter } from "@tanstack/react-router";
import { useServerFn } from "#/modules/util.hooks/use-server-fn";
import { useCallback, useEffect, useRef, useState } from "react";
import { z } from "zod/v4";
import { HistoryBackLink } from "#/components/back-link";
import { ErrorBoundaryComponent } from "#/components/error-boundary";
import { Page } from "#/components/page";
import { TicketMessageThread } from "#/modules/tickets/components/ticket-message-thread";
import { TicketComposer } from "#/modules/tickets/components/ticket-composer";
import { TicketMessageEditDrawer } from "#/modules/tickets/components/ticket-message-edit-drawer";
import { TicketStatusBadge } from "#/modules/tickets/components/ticket-status-badge";
import {
  addTicketMessageFn,
  closeTicketFn,
  deleteTicketMessageFn,
  editTicketMessageFn,
  getTicketFn,
  markTicketReadFn,
  uploadTicketFileFn,
} from "#/modules/tickets/tickets.functions";
import { useMessageEditing } from "#/modules/tickets/use-message-editing";
import { useTicketEvents } from "#/modules/tickets/use-ticket-events";
import { buildLocaleHref, useLocaleParams } from "#/modules/util.helpers/route-href";
import { m } from "#/paraglide/messages";
import { genericFormError } from "#/modules/util.errors/translate";

export const Route = createFileRoute("/$locale/{-$schoolyear}/_authenticated/$school/tickets/$id")({
  loader: ({ params }) => {
    // The id comes straight from the URL: a mangled link is a ticket that does not exist.
    if (!z.uuid().safeParse(params.id).success) throw notFound();
    return getTicketFn({ data: { id: params.id } });
  },
  component: UserTicketDetail,
  errorComponent: UserTicketDetailError,
  notFoundComponent: UserTicketDetailNotFound,
});

function UserTicketDetailNotFound() {
  return (
    <Page size="sm">
      <Page.Header>
        <HistoryBackLink
          fallback={{ from: Route.fullPath, to: ".." }}
          className="max-md:self-start max-sm:mb-4"
        />
      </Page.Header>
      <Page.Container>
        <Page.Body>
          <Empty>
            <EmptyHeader>
              <EmptyTitle>{m["tickets.detail.not_found_title"]()}</EmptyTitle>
              <EmptyDescription>{m["tickets.detail.not_found_description"]()}</EmptyDescription>
            </EmptyHeader>
          </Empty>
        </Page.Body>
      </Page.Container>
    </Page>
  );
}

function UserTicketDetailError({ error }: { error: unknown }) {
  const params = Route.useParams();
  return <ErrorBoundaryComponent error={error} params={params} />;
}

/** Mirrors the admin drawer's close button; there is no reopen side — replying reopens. */
function CloseTicketButton({ ticketId }: { ticketId: string }) {
  const router = useRouter();
  const closeTicket = useServerFn(closeTicketFn);
  const [isPending, setIsPending] = useState(false);

  const handleClick = async () => {
    setIsPending(true);
    try {
      await closeTicket({ data: { id: ticketId } });
      await router.invalidate();
    } catch {
      // toastErrorMiddleware already surfaced the error.
    } finally {
      setIsPending(false);
    }
  };

  return (
    <Button variant="outline" isPending={isPending} onClick={() => void handleClick()}>
      <IconMaterialSymbolsLockOutline />
      {m["tickets.detail.close_button"]()}
    </Button>
  );
}

function UserTicketDetail() {
  const ticket = Route.useLoaderData();
  const router = useRouter();
  const localeParams = useLocaleParams();
  const markRead = useServerFn(markTicketReadFn);
  const addMessage = useServerFn(addTicketMessageFn);
  const uploadFile = useServerFn(uploadTicketFileFn);
  const editMessage = useServerFn(editTicketMessageFn);
  const deleteMessage = useServerFn(deleteTicketMessageFn);
  const marked = useRef<string | null>(null);
  const editing = useMessageEditing(ticket.messages);

  const markTicketRead = useCallback(async () => {
    await markRead({ data: { id: ticket.id } });
  }, [markRead, ticket.id]);

  // Mirrors the release-notes "mark viewed on open" pattern: fire once per ticket, then refresh
  // the root context so the user-menu unread badge/dot clears without a full reload.
  useEffect(() => {
    if (marked.current === ticket.id) return;
    marked.current = ticket.id;
    void markTicketRead().then(() => router.invalidate());
  }, [ticket.id, markTicketRead, router]);

  useTicketEvents(ticket.id, "user", markTicketRead);

  const uploadTicketFile = async (formData: FormData) => {
    const result = await uploadFile({ data: formData });
    if (!result) return genericFormError("UploadResultMissing");
    if ("formError" in result) return result;
    return { file: result.file };
  };

  return (
    <Page size="sm">
      <Page.Header>
        <HistoryBackLink
          fallback={{ from: Route.fullPath, to: ".." }}
          className="max-md:self-start max-sm:mb-4"
        />
        <Page.Title>
          #{ticket.ticketNumber} {ticket.subject}
        </Page.Title>
        <div className="ml-auto flex items-center gap-2">
          <TicketStatusBadge status={ticket.status} />
        </div>
      </Page.Header>
      <Page.Container>
        <Page.Body className="flex flex-col gap-6">
          <TicketMessageThread
            messages={ticket.messages}
            viewerRole="User"
            getAttachmentUrl={(fileId) =>
              buildLocaleHref(`/api/user/tickets/${ticket.id}/files/${fileId}`, localeParams)
            }
            editingId={editing.open ? editing.message?.id : undefined}
            onEdit={editing.startEditing}
            onDelete={async (message) => {
              await deleteMessage({ data: { id: ticket.id, messageId: message.id } });
              await router.invalidate();
            }}
          />

          {ticket.status === "Closed" ? (
            <p className="bg-muted text-muted-foreground rounded-lg px-4 py-3 text-sm">
              {m["tickets.detail.closed_banner"]()}
            </p>
          ) : null}

          <TicketComposer
            actions={ticket.status === "Open" && <CloseTicketButton ticketId={ticket.id} />}
            placeholder={m["tickets.detail.reply_placeholder"]()}
            onArrowUpWhenEmpty={editing.editLast}
            uploadFile={uploadTicketFile}
            onSubmit={(data) => addMessage({ data: { id: ticket.id, ...data } })}
            onSuccess={() => {
              void router.invalidate();
            }}
          />

          <TicketMessageEditDrawer
            editing={editing}
            uploadFile={uploadTicketFile}
            onSave={(messageId, data) =>
              editMessage({ data: { id: ticket.id, messageId, ...data } })
            }
            onSaved={() => {
              void router.invalidate();
            }}
          />
        </Page.Body>
      </Page.Container>
    </Page>
  );
}
